import { useState, useEffect, useRef } from 'react';
import { config } from '../config';
import { imageServerConfig } from '../config/imageServer';

interface ServerHealthStatus {
  healthy: boolean;
  error: string | null;
  lastChecked: Date | null;
}

interface ServerHealth {
  backend: ServerHealthStatus;
  imageServer: ServerHealthStatus;
}

const POLL_INTERVAL = 5000; // 5 seconds

const checkBackendHealth = async (): Promise<ServerHealthStatus> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000);
  
  const healthUrl = `${config.apiBaseUrl}/health`;
  console.log('[Health Check] Backend URL:', healthUrl);
  
  try {
    const response = await fetch(healthUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Use a shorter timeout for health checks
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      return {
        healthy: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
        lastChecked: new Date(),
      };
    }

    const data = await response.json();
    
    // Check if status indicates health
    const isHealthy = data.status === 'OK' || data.status === 'healthy';
    
    return {
      healthy: isHealthy,
      error: isHealthy ? null : `Status: ${data.status}`,
      lastChecked: new Date(),
    };
  } catch (error) {
    clearTimeout(timeoutId);
    let errorMessage = 'Unknown error';
    
    if (error instanceof Error) {
      console.error('[Health Check] Backend error:', error.name, error.message, 'URL:', healthUrl);
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        errorMessage = 'Connection timeout';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Network error - server may be down';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error - server may not be running';
      } else if (error.message.includes('certificate') || error.message.includes('SSL') || error.message.includes('TLS')) {
        errorMessage = 'Certificate error - check SSL configuration';
      } else {
        errorMessage = error.message;
      }
    }
    
    return {
      healthy: false,
      error: errorMessage,
      lastChecked: new Date(),
    };
  }
};

const checkImageServerHealth = async (): Promise<ServerHealthStatus> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 3000);
  
  const healthUrl = `${imageServerConfig.baseUrl}/health`;
  console.log('[Health Check] Image Server URL:', healthUrl);
  
  try {
    const response = await fetch(healthUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Use a shorter timeout for health checks
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      return {
        healthy: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
        lastChecked: new Date(),
      };
    }

    const data = await response.json();
    
    // Check if status indicates health
    const isHealthy = data.status === 'healthy' || data.status === 'OK';
    
    return {
      healthy: isHealthy,
      error: isHealthy ? null : `Status: ${data.status}`,
      lastChecked: new Date(),
    };
  } catch (error) {
    clearTimeout(timeoutId);
    let errorMessage = 'Unknown error';
    
    if (error instanceof Error) {
      console.error('[Health Check] Image Server error:', error.name, error.message, 'URL:', healthUrl);
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        errorMessage = 'Connection timeout';
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage = 'Network error - server may be down';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error - server may not be running';
      } else if (error.message.includes('certificate') || error.message.includes('SSL') || error.message.includes('TLS')) {
        errorMessage = 'Certificate error - check SSL configuration';
      } else {
        errorMessage = error.message;
      }
    }
    
    return {
      healthy: false,
      error: errorMessage,
      lastChecked: new Date(),
    };
  }
};

export const useServerHealth = () => {
  const [health, setHealth] = useState<ServerHealth>({
    backend: { healthy: false, error: null, lastChecked: null },
    imageServer: { healthy: false, error: null, lastChecked: null },
  });
  const intervalRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  const checkHealth = async () => {
    const [backendStatus, imageServerStatus] = await Promise.all([
      checkBackendHealth(),
      checkImageServerHealth(),
    ]);

    // Avoid setState after unmount (e.g. in tests when env is torn down)
    if (mountedRef.current && typeof window !== 'undefined') {
      setHealth({
        backend: backendStatus,
        imageServer: imageServerStatus,
      });
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    // Initial check
    checkHealth();

    // Set up polling
    intervalRef.current = window.setInterval(() => {
      checkHealth();
    }, POLL_INTERVAL);

    return () => {
      mountedRef.current = false;
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    health,
    checkHealth,
    backendUrl: config.apiBaseUrl,
    imageServerUrl: imageServerConfig.baseUrl,
  };
};
